import { Controller } from '@hotwired/stimulus';
import Axios from '../axios';
import { getMeta } from '../helpers';

export default class extends Controller {
    setupFormAction(action, actionData) {
        switch (action) {
            case 'html':
                this.element.innerHTML = actionData;
                this.element.classList.remove('loading');
                break;
        }
    }

    connect() {
        this.currentLanguageCode = document.documentElement.lang.split('-')[0];
        this.element.addEventListener('submit', async (e) => {
            e.preventDefault();

            if (this.element.classList.contains('loading')) {
                return;
            }

            this.element.classList.add('loading');

            const url = `${this.element.action}?rest_language=${this.currentLanguageCode}`;
            const method = this.element.method.toLowerCase();
            const data = new FormData(this.element);

            let response = null;

            try {
                if (method === 'get') {
                    response = await Axios.get(url);
                } else if (method === 'post') {
                    response = await this.handlePostSubmit(url, data, { headers: { 'Content-Type': 'multipart/form-data' } });
                }

                response = response.data;
            } catch (error) {
                response = error.response.data.data;
            }

            this.removeFormNotes();

            if (response && response.errors && Object.keys(response.errors).length) {
                this.setupFormErrors(response.errors);
            } else if (response && response.general_error) {
                this.setupFormGeneralError(response.general_error);
            } else if (response && response.general_success) {
                this.setupFormGeneralSuccess(response.general_success);
            } else if (response && response.redirect) {
                window.location.href = response.redirect;
            } else if (response && response.reload) {
                location.href = location.pathname + '?logged_in';
            } else if (response && response.action) {
                this.setupFormAction(response.action, response.action_data);
            } else {
                this.setupFormUnexpectedError();
            }
        });
    }

    handlePostSubmit(url, data, config) {
        if (window.grecaptcha && data instanceof FormData) {
            return new Promise((resolve) => {
                window.grecaptcha.ready(() => {
                    resolve(window.grecaptcha.execute(getMeta('recaptcha_site_key'), {
                        action: 'submit'
                    }));
                });
            }).then(token => {
                data.append('token', token);
                return Axios.post(url, data, config);
            });
        }

        return Axios.post(url, data, config);
    }

    setupFormUnexpectedError() {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-error');
        messageNode.innerHTML = getMeta('global_axios_validator_error');
        this.element.appendChild(messageNode);
        this.element.classList.remove('loading');
    }

    setupFormGeneralSuccess(success) {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-success');
        messageNode.innerHTML = success;
        this.element.appendChild(messageNode);
        this.element.classList.remove('loading');
    }

    setupFormGeneralError(error) {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-error');
        messageNode.innerHTML = error;
        this.element.appendChild(messageNode);
        this.element.classList.remove('loading');
    }

    setupFormErrors(errors) {
        let topMostError = null;

        for (let i = 0, keys = Object.keys(errors), ii = keys.length; i < ii; i++) {
            const input = this.element.querySelector(`[name='${keys[i]}']`);

            if (input) {
                input.classList.add('invalid');

                const messageNode = document.createElement('span');
                messageNode.classList.add('input-error');
                messageNode.innerHTML = errors[keys[i]];

                if (input.type === "checkbox") {
                    input.parentNode.appendChild(messageNode);
                } else {
                    input.parentNode.insertBefore(messageNode, input.nextSibling);
                }

                if (topMostError) {
                    if (topMostError.getBoundingClientRect().top > input.getBoundingClientRect().top) {
                        topMostError = input;
                    }
                } else {
                    topMostError = input;
                }
            }
        }

        this.element.classList.remove('loading');

        const firstInvalid = this.element.querySelector('.invalid');
        if (firstInvalid) {
            this.scrollToElementWithOffset(firstInvalid, 60);
        }
    }

   scrollToElementWithOffset(element, offset) {
        const modal = this.element.closest('.modal-scroll-container');
        if (modal) {
            const targetPosition = element.getBoundingClientRect().top - modal.getBoundingClientRect().top + modal.scrollTop - offset;
            modal.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        } else {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = element.getBoundingClientRect().top + scrollTop - offset;

            window.scrollTo({
                top: targetPosition - window.innerHeight / 2,
                behavior: 'smooth'
            });
        }
    }

    removeFormNotes() {
        const generalErrors = this.element.querySelectorAll('.general-error');
        for (const el of generalErrors) {
            if (!el.classList.contains('axios-no-remove')) {
                el.remove();
            }
        }

        const generalSuccess = this.element.querySelectorAll('.general-success');
        for (const el of generalSuccess) {
            if (!el.classList.contains('axios-no-remove')) {
                el.remove();
            }
        }

        const fields = this.element.querySelectorAll('.input');
        for (const el of fields) {
            el.classList.remove('invalid');
        }

        const messageFields = this.element.querySelectorAll('.input-error');
        for (const el of messageFields) {
            el.remove();
        }
    }
}
